/**
 * BedIcon - Component modular per mostrar icones de llits SVG
 * Els SVGs estan a /public/icons/calendar/beds/
 * 
 * @example
 * <BedIcon type="double" />
 * <BedIcon type="single" className="text-blue-400" />
 * <BedIcon type="extra" /> // Sempre vermell
 */

import { cn } from '@/lib/utils';

export type BedType = 'single' | 'double' | 'crib' | 'bunk' | 'extra';

interface BedIconProps {
  type: BedType;
  className?: string;
  /** En gris (reserva cancel·lada); guanya a `className` i al vermell del supletori. */
  muted?: boolean;
}

// Colors per defecte per cada tipus de llit. Tons apagats (saturació ≤ 0,46, ≥ 3:1 contra blanc):
// la barra del canal ha de ser el color més viu de la card (`plans/calendar-visual-refresh.md` §6.6).
const BED_COLORS: Record<BedType, string> = {
  single: 'text-[#6B8CC0]',
  double: 'text-[#7479B8]',
  crib: 'text-[#B97A96]',
  bunk: 'text-[#B8834F]',
  extra: 'text-[#C25A5A]', // Sempre vermell (apagat)
};

/** Reserva cancel·lada: tots els llits en gris, també el supletori. */
const MUTED_COLOR = 'text-gray-400';

// Mides dels SVGs
const BED_SIZES: Record<BedType, { width: number; height: number }> = {
  single: { width: 14, height: 14 },
  double: { width: 28, height: 14 },
  crib: { width: 14, height: 14 },
  bunk: { width: 14, height: 14 },
  extra: { width: 14, height: 14 },
};

export function BedIcon({ type, className, muted }: BedIconProps) {
  const size = BED_SIZES[type];
  const defaultColor = BED_COLORS[type];

  // Per 'extra', sempre vermell (no es pot sobreescriure); només `muted` el passa a gris
  const colorClass = muted
    ? MUTED_COLOR
    : type === 'extra'
      ? BED_COLORS.extra
      : className || defaultColor;

  return (
    <svg
      width={size.width}
      height={size.height}
      viewBox={`0 0 ${size.width} 14`}
      className={cn('inline-block flex-shrink-0', colorClass)}
      aria-label={`Llit ${type}`}
    >
      {type === 'single' && (
        <>
          <rect width="14" height="14" rx="2" fill="currentColor" />
          <rect x="2" y="2" width="10" height="4" rx="1" fill="currentColor" opacity="0.5" />
        </>
      )}
      {type === 'double' && (
        <>
          <rect width="28" height="14" rx="2" fill="currentColor" />
          <rect x="2" y="2" width="10" height="4" rx="1" fill="currentColor" opacity="0.5" />
          <rect x="16" y="2" width="10" height="4" rx="1" fill="currentColor" opacity="0.5" />
        </>
      )}
      {type === 'crib' && (
        <>
          <rect width="14" height="14" rx="2" fill="currentColor" />
          <path d="M4 2V12 M7 2V12 M10 2V12" stroke="white" strokeWidth="1" strokeOpacity="0.5" />
        </>
      )}
      {type === 'bunk' && (
        <>
          <rect width="14" height="14" rx="2" fill="currentColor" />
          <line x1="0" y1="7" x2="14" y2="7" stroke="white" strokeOpacity="0.5" />
          <rect x="2" y="2" width="10" height="2" rx="0.5" fill="currentColor" opacity="0.5" />
          <rect x="2" y="9" width="10" height="2" rx="0.5" fill="currentColor" opacity="0.5" />
        </>
      )}
      {type === 'extra' && (
        <>
          <rect width="14" height="14" rx="2" fill="currentColor" />
          <rect x="2" y="2" width="10" height="4" rx="1" fill="currentColor" opacity="0.5" />
          <path d="M7 9V13 M5 11H9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/**
 * BedIcons - Renderitza múltiples llits a partir d'un array
 * 
 * @example
 * <BedIcons beds={['double', 'single', 'crib']} />
 */
interface BedIconsProps {
  beds: BedType[];
  className?: string;
  gap?: 'sm' | 'md';
  /** Tots els llits en gris (reserva cancel·lada). */
  muted?: boolean;
}

export function BedIcons({ beds, className, gap = 'sm', muted }: BedIconsProps) {
  const gapClass = gap === 'sm' ? 'gap-0.5' : 'gap-1';
  
  return (
    <div className={cn('flex items-center', gapClass, className)}>
      {beds.map((bed, index) => (
        <BedIcon key={`${bed}-${index}`} type={bed} muted={muted} />
      ))}
    </div>
  );
}

export default BedIcon;
